/**
 * ContagensListView Component - Shows list of contagens (jobs) with their items
 *
 * Epic 45 - Enhanced stock view with real contagem data
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, ChevronRight, ChevronDown, Calendar, Building2, MapPin, User,
  RefreshCw, Barcode as BarcodeIcon, QrCode, ScanLine, Keyboard, Filter, Users
} from 'lucide-react';
import jobService from '../../services/jobService';
import { MultiSelectFilter } from '../MultiSelectFilter';
import { JobItemsView } from './JobItemsView';

interface Job {
  id: number;
  titulo: string;
  hospital_nome: string;
  location_nome: string;
  status: string;
  data_prevista: string;
  total_items_counted: number;
  total_items_expected: number;
  total_quantity_counted: number;
  clientes?: Array<{id: number; nome: string; nome_fantasia?: string; codigo: string}>;
}

interface ContagemItem {
  id: number;
  codigo_produto: string;
  descricao: string;
  lote: string;
  data_validade: string;
  quantidade_sistema: number;
  gtin?: string;
  gtin_cadastro?: string;
  codigo_cliente_produto?: string;
  quantidade_coletar?: number;
  unidade: string;
  faixa_aging?: string;
  dias_para_vencer?: number;
  status_processamento?: string;
  cliente_nome_fantasia?: string;
  com_regime?: boolean;
}

interface ContagensListViewProps {
  viewMode: 'cards' | 'list';
  formatDate: (dateString: string) => string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'EXECUTADA': return 'bg-green-100 text-green-800';
    case 'EM_EXECUCAO': return 'bg-blue-100 text-blue-800';
    case 'CONFIRMADA': return 'bg-purple-100 text-purple-800';
    case 'AGENDADA': return 'bg-yellow-100 text-yellow-800';
    case 'PRE_AGENDADA': return 'bg-orange-100 text-orange-800';
    case 'NAO_EXECUTADA': return 'bg-red-100 text-red-800';
    case 'NAO_AUTORIZADA': return 'bg-gray-100 text-gray-800';
    case 'CANCELADA': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'EXECUTADA': return 'Executada';
    case 'EM_EXECUCAO': return 'Em Execução';
    case 'CONFIRMADA': return 'Confirmada';
    case 'AGENDADA': return 'Agendada';
    case 'PRE_AGENDADA': return 'Pré-Agendada';
    case 'NAO_EXECUTADA': return 'Não Executada';
    case 'NAO_AUTORIZADA': return 'Não Autorizada';
    case 'CANCELADA': return 'Cancelada';
    default: return status;
  }
};

export const ContagensListView: React.FC<ContagensListViewProps> = ({ viewMode, formatDate }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [jobItems, setJobItems] = useState<Map<number, ContagemItem[]>>(new Map());
  const [loadingItems, setLoadingItems] = useState<number | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'with_items'>('with_items');
  const [localSelectedClientes, setLocalSelectedClientes] = useState<(string | number)[]>([]);

  // Build cliente options from jobs
  const jobClienteOptions = useMemo(() => {
    const clientesMap = new Map<number, { id: number; nome: string; nome_fantasia?: string }>();
    jobs.forEach(job => {
      job.clientes?.forEach(c => {
        if (c.id && !clientesMap.has(c.id)) clientesMap.set(c.id, c);
      });
    });
    return Array.from(clientesMap.values())
      .map(c => ({ value: c.id, label: c.nome_fantasia || c.nome || `Cliente ${c.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs]);

  const clientFilteredJobs = localSelectedClientes.length > 0
    ? jobs.filter(job => job.clientes?.some(c => localSelectedClientes.includes(c.id)))
    : jobs;

  const filteredJobs = filterMode === 'all'
    ? clientFilteredJobs
    : clientFilteredJobs.filter(j => j.total_items_expected > 0 || j.total_items_counted > 0);

  const jobsWithItems = clientFilteredJobs.filter(j => j.total_items_expected > 0 || j.total_items_counted > 0).length;

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await jobService.getJobs({ page_size: 100 });
      setJobs(response.items);
    } catch (error) {
      console.error('Erro ao carregar contagens:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobItems = async (jobId: number) => {
    if (jobItems.has(jobId)) {
      setExpandedJobId(expandedJobId === jobId ? null : jobId);
      return;
    }
    try {
      setLoadingItems(jobId);
      const response = await jobService.getExpectedItems(jobId);
      const items = response.results || [];
      setJobItems(new Map(jobItems.set(jobId, items)));
      setExpandedJobId(jobId);
    } catch (error) {
      console.error(`Erro ao carregar itens esperados do job ${jobId}:`, error);
      setJobItems(new Map(jobItems.set(jobId, [])));
      setExpandedJobId(jobId);
    } finally {
      setLoadingItems(null);
    }
  };

  const filterItemsByClient = (items: ContagemItem[]): ContagemItem[] => {
    if (localSelectedClientes.length === 0) return items;
    const selectedNames = new Set<string>();
    localSelectedClientes.forEach(clienteId => {
      const option = jobClienteOptions.find(o => o.value === clienteId);
      if (option) selectedNames.add(option.label);
    });
    return items.filter(item => !item.cliente_nome_fantasia || selectedNames.has(item.cliente_nome_fantasia));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Info banner */}
      <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
        <div className="flex items-start gap-3">
          <Box className="w-5 h-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-green-900">
              Simulador de Contagens - Dados REAIS ({filteredJobs.length} de {clientFilteredJobs.length} contagens
              {localSelectedClientes.length > 0 ? ` • Filtro: ${localSelectedClientes.length} cliente(s)` : ''})
            </h3>
            <p className="text-sm text-green-700 mt-1">
              Selecione uma contagem para simular a leitura de códigos de barras. Mostrando itens do SISTEMA (a serem contados).
            </p>

            {jobClienteOptions.length > 0 && (
              <div className="mt-3 max-w-xs">
                <div className="flex items-center gap-2 mb-1 text-sm text-gray-700">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Filtrar por Cliente:</span>
                </div>
                <MultiSelectFilter
                  label=""
                  options={jobClienteOptions}
                  selectedValues={localSelectedClientes}
                  onChange={setLocalSelectedClientes}
                  placeholder="Todos os clientes..."
                  showTodos={true}
                  showBadges={true}
                />
              </div>
            )}

            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Filter className="w-4 h-4" />
                <span>Mostrar:</span>
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                <button
                  onClick={() => setFilterMode('with_items')}
                  className={`px-3 py-1 text-sm font-medium transition ${filterMode === 'with_items' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  Com Itens ({jobsWithItems})
                </button>
                <button
                  onClick={() => setFilterMode('all')}
                  className={`px-3 py-1 text-sm font-medium transition ${filterMode === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  Todas ({clientFilteredJobs.length})
                </button>
              </div>
            </div>
            <div className="flex gap-3 mt-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                <ScanLine className="w-3 h-3" /> A = 2 Leituras
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                <BarcodeIcon className="w-3 h-3" /> B = GS1-128
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                <QrCode className="w-3 h-3" /> C = DataMatrix
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                <Keyboard className="w-3 h-3" /> D = Apenas GTIN
              </span>
            </div>
          </div>
          <button
            onClick={loadJobs}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Jobs list */}
      <div className="space-y-4">
        {filteredJobs.map((job) => {
          const isExpanded = expandedJobId === job.id;
          const rawItems = jobItems.get(job.id) || [];
          const items = filterItemsByClient(rawItems);
          const isLoadingThisJob = loadingItems === job.id;
          const hasExpectedItems = job.total_items_expected > 0;

          return (
            <div
              key={job.id}
              className={`bg-white rounded-lg shadow-md border overflow-hidden ${hasExpectedItems ? 'border-green-300' : 'border-gray-200'}`}
            >
              <div className="p-4 cursor-pointer hover:bg-gray-50 transition" onClick={() => loadJobItems(job.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-green-100' : hasExpectedItems ? 'bg-green-50' : 'bg-gray-100'}`}>
                      {isLoadingThisJob ? (
                        <RefreshCw className="w-5 h-5 text-green-600 animate-spin" />
                      ) : isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-green-600" />
                      ) : (
                        <ChevronRight className={`w-5 h-5 ${hasExpectedItems ? 'text-green-600' : 'text-gray-600'}`} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">#{job.id}</span>
                        <span className="text-gray-700 font-medium">{job.titulo}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(job.status)}`}>
                          {getStatusLabel(job.status)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />{job.hospital_nome}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />{job.location_nome || '-'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />{formatDate(job.data_prevista)}
                        </span>
                        {job.clientes && job.clientes.length > 0 && (
                          <span className="flex items-center gap-1 text-amber-600">
                            <User className="w-4 h-4" />{job.clientes.map(c => c.nome).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${hasExpectedItems ? 'text-green-600' : 'text-gray-400'}`}>
                      {job.total_items_expected} itens no sistema
                    </div>
                    <div className="text-sm text-gray-500">
                      {job.total_items_counted > 0 ? (
                        <span className="text-purple-600">{job.total_items_counted} já contados</span>
                      ) : (
                        <span>Nenhum contado</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <JobItemsView
                    items={items}
                    rawItemsCount={rawItems.length}
                    viewMode={viewMode}
                    formatDate={formatDate}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredJobs.length === 0 && (
        <div className="text-center py-12">
          <Box className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          {filterMode === 'with_items' ? (
            <>
              <p className="text-gray-600 mb-2">Nenhuma contagem com itens no sistema.</p>
              <p className="text-sm text-gray-500">Clique em "Todas" para ver contagens sem itens atribuídos.</p>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-2">Nenhuma contagem encontrada.</p>
              <p className="text-sm text-gray-500">As contagens são criadas na página de Calendário.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
